import {compact} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode} from 'react';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {InboxesFormatter} from '../../components/common/formatters/inboxesFormatter';
import {InboxModel} from '../../models/api/inboxModel';
import {
  AdvancedNotificationRule,
  TeammateAdvancedNotificationNamesEnum,
} from '../../models/api/notificationRulesModel';
import {ResourceMap} from '../../store/appTypes';

export function buildAdvancedNotificationRuleDescription(
  rule: AdvancedNotificationRule,
  inboxes: ResourceMap<InboxModel>,
  BoldWrapper: FC<PropsWithChildren<{}>> = (props) => <>{props.children}</>,
) {
  const inboxesPart = renderInboxesPart(rule, inboxes);

  switch (rule.name) {
    case TeammateAdvancedNotificationNamesEnum.CONVERSATION_ASSIGNED: {
      return (
        <Trans id="advancednotificationrule-conversation-assigned">
          If I am assigned a new conversation from <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.INBOUND_ANY_MESSAGE: {
      return (
        <Trans id="advancednotificationrule-inbound-any-message">
          If a message arrives in <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.MENTION: {
      return (
        <Trans id="advancednotificationrule-mention">
          If I am @mentioned in a comment in <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.OUTBOUND_ANY_MESSAGE: {
      return (
        <Trans id="advancednotificationrule-outbound-any-message">
          If a new message is sent from <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.POST_IN_ASSIGNED: {
      return (
        <Trans id="advancednotificationrule-post-in-assigned">
          If a new activity occurs in my assigned conversation from <BoldWrapper>{inboxesPart}</BoldWrapper>,
          then {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.POST_IN_FOLLOWED: {
      return (
        <Trans id="advancednotificationrule-post-in-followed">
          If a new activity occurs in a conversation I&apos;m subscribed to in{' '}
          <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.RECIPIENT_ERROR: {
      return (
        <Trans id="advancednotificationrule-recipient-error">
          If a sent message failed to be delivered from <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.REMINDER: {
      return (
        <Trans id="advancednotificationrule-reminder">
          If I receive a reminder for a snoozed conversation in <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.RULE_NOTIFY: {
      return (
        <Trans id="advancednotificationrule-rule-notify">
          If I am notified by a rule, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }
    case TeammateAdvancedNotificationNamesEnum.TRACKER_SHARE: {
      return (
        <Trans id="advancednotificationrule-tracker-share">
          If I am invited to a new conversation in <BoldWrapper>{inboxesPart}</BoldWrapper>, then{' '}
          {renderAdvancedNotificationRuleSettingPart(rule.setting, BoldWrapper)}
        </Trans>
      );
    }

    default: {
      throw new Error('This should not happen');
    }
  }
}

enum NotificationCombinedDestinationsEnum {
  DESKTOP = 'DESKTOP',
  EMAIL = 'EMAIL',
  MOBILE = 'MOBILE',
  DESKTOP_EMAIL = 'DESKTOP_EMAIL',
  DESKTOP_MOBILE = 'DESKTOP_MOBILe',
  MOBILE_EMAIL = 'MOBILE_EMAIL',
  DESKTOP_MOBILE_EMAIL = 'DESKTOP_MOBILE_EMAIL',
  NONE = 'NONE',
}

function getEnumForAdvancedNotificationRuleSetting(
  setting: AdvancedNotificationRule['setting'],
): NotificationCombinedDestinationsEnum {
  const values = [setting.app, setting.email, setting.mobile].toString();

  switch (values) {
    case [true, false, false].toString(): {
      return NotificationCombinedDestinationsEnum.DESKTOP;
    }
    case [false, true, false].toString(): {
      return NotificationCombinedDestinationsEnum.EMAIL;
    }
    case [false, false, true].toString(): {
      return NotificationCombinedDestinationsEnum.MOBILE;
    }
    case [true, true, false].toString(): {
      return NotificationCombinedDestinationsEnum.DESKTOP_EMAIL;
    }
    case [false, true, true].toString(): {
      return NotificationCombinedDestinationsEnum.MOBILE_EMAIL;
    }
    case [true, false, true].toString(): {
      return NotificationCombinedDestinationsEnum.DESKTOP_MOBILE;
    }
    case [true, true, true].toString(): {
      return NotificationCombinedDestinationsEnum.DESKTOP_MOBILE_EMAIL;
    }
    default: {
      return NotificationCombinedDestinationsEnum.NONE;
    }
  }
}

export function renderAdvancedNotificationRuleSettingPart(
  setting: AdvancedNotificationRule['setting'],
  BoldWrapper: FC<PropsWithChildren<{}>> = (props) => <>{props.children}</>,
) {
  const combinedDestinationEnum = getEnumForAdvancedNotificationRuleSetting(setting);
  const destinationsRenderers: {[k in NotificationCombinedDestinationsEnum]: ReactNode} = {
    [NotificationCombinedDestinationsEnum.DESKTOP]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-desktop">on desktop</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.EMAIL]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-email">by email</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.MOBILE]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-mobile">on mobile</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.DESKTOP_EMAIL]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-desktopemail">on desktop and email</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.MOBILE_EMAIL]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-mobileemail">on mobile and email</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.DESKTOP_MOBILE]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-desktopmobile">on desktop and mobile</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.DESKTOP_MOBILE_EMAIL]: (
      <>
        <Trans id="advancednotificationrule-notifyme">notify me</Trans>{' '}
        <BoldWrapper>
          <Trans id="advancednotificationrule-desktopmobileemail">on desktop, mobile and email</Trans>
        </BoldWrapper>
      </>
    ),
    [NotificationCombinedDestinationsEnum.NONE]: (
      <BoldWrapper>
        <Trans id="advancednotificationrule-none">don&apos;t notify me</Trans>
      </BoldWrapper>
    ),
  };

  return destinationsRenderers[combinedDestinationEnum];
}

function renderInboxesPart(rule: AdvancedNotificationRule, inboxesMap: ResourceMap<InboxModel>) {
  if (rule.isInAllInboxes) {
    return <Trans id="advancednotificationrule-anyinbox">any inbox</Trans>;
  }

  const inboxes = compact(rule.inboxIds.map((i) => inboxesMap[i]));

  return (
    <InboxesFormatter
      inboxes={inboxes}
      limit={3}
      renderConjunction={(visible, other) => (
        <Trans id="advancednotificationrule-inboxconjunction">
          {visible} or {other}
        </Trans>
      )}
    />
  );
}
